import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowRight, Loader2, Check, Instagram, Facebook, Phone, Music2,
  Calendar as CalendarIcon, UploadCloud, X, Film, Image as ImageIcon,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICES, BOOKING_TIMES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "", email: "", phone: "", service: "",
  property_details: "", message: "", callout: false,
};

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [files, setFiles] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const MAX_FILES = 20;
  const MAX_SIZE = 200 * 1024 * 1024;

  const addFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    const tooBig = chosen.filter((f) => f.size > MAX_SIZE);
    const valid = chosen.filter((f) => f.size <= MAX_SIZE);
    if (tooBig.length) toast.error(`${tooBig.length} file(s) over 200MB were skipped. Try trimming long videos.`);
    setFiles((prev) => {
      const room = MAX_FILES - prev.length;
      if (room <= 0) {
        toast.error(`You can attach up to ${MAX_FILES} files.`);
        return prev;
      }
      if (valid.length > room) toast.error(`Only ${room} more file(s) could be added (max ${MAX_FILES}).`);
      return [...prev, ...valid.slice(0, room)];
    });
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      // 1. Upload any attachments first
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await fetch(`${API}/uploads`, { method: "POST", body: fd });
        if (!up.ok) throw new Error("upload");
        const data = await up.json();
        paths.push(data.path);
      }
      // 2. Create the booking
      const payload = {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : "",
        preferred_time: time,
        attachment_paths: paths,
      };
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("booking");
      setDone(true);
      setForm(emptyForm); setDate(null); setTime(""); setFiles([]);
      toast.success("Booking request sent — Katie will be in touch!");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call 07562 245578.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-espresso/20 py-3 text-espresso placeholder:text-cocoa/50 focus:outline-none focus:border-blush transition-colors duration-300 font-light";
  const labelCls = "text-xs uppercase tracking-[0.2em] text-cocoa";

  return (
    <section id="contact" className="bg-cream py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left column */}
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.28em] text-blush mb-6">(Book your clean)</p>
          <h2 className="font-serif font-light text-espresso text-4xl md:text-6xl leading-[1.02] tracking-tight mb-8">
            Request your <span className="italic text-blush">booking</span>.
          </h2>
          <p className="text-lg font-light text-muted-foreground leading-relaxed max-w-md mb-6">
            Pick a service, your preferred day & time, and send a few photos or a quick walk-through video so I can
            prepare an accurate quote. Prefer a call-out visit instead? Just tick the box.
          </p>
          <div className="inline-flex items-center gap-3 bg-blush-light text-cocoa px-5 py-3 mb-10">
            <span className="text-xs uppercase tracking-[0.18em]">A small deposit is arranged by message once your booking is confirmed</span>
          </div>
          <div className="space-y-4 text-cocoa font-light">
            <a href="tel:07562245578" data-testid="contact-phone-link" className="flex items-center gap-4 group w-fit">
              <span className="h-11 w-11 rounded-full border border-espresso/20 flex items-center justify-center group-hover:bg-espresso group-hover:text-cream transition-colors duration-300"><Phone size={17} /></span>
              <span><span className="block uppercase tracking-[0.2em] text-xs text-blush">Book Now</span>07562 245578</span>
            </a>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/klean_withkatie?igsi=MW52YW45YWt3ejhncA%3D%3D&utm_source=qr" target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="h-11 w-11 rounded-full border border-espresso/20 flex items-center justify-center hover:bg-espresso hover:text-cream transition-colors duration-300"><Instagram size={17} /></a>
              <a href="https://www.facebook.com/share/1D36HFTaeK/?mibextid=wwXIfr" target="_blank" rel="noreferrer" data-testid="contact-facebook-link" className="h-11 w-11 rounded-full border border-espresso/20 flex items-center justify-center hover:bg-espresso hover:text-cream transition-colors duration-300"><Facebook size={17} /></a>
              <a href="https://www.tiktok.com/@kleanwithkatie3?_r=1&_t=ZN-998Fb48LmLe" target="_blank" rel="noreferrer" data-testid="contact-tiktok-link" className="h-11 w-11 rounded-full border border-espresso/20 flex items-center justify-center hover:bg-espresso hover:text-cream transition-colors duration-300"><Music2 size={17} /></a>
              <span className="text-sm"><span className="block uppercase tracking-[0.2em] text-xs text-blush">Follow</span>@KleanwithKatie</span>
            </div>
          </div>
        </div>

        {/* Right column: form */}
        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              data-testid="contact-success"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="h-full min-h-[400px] flex flex-col items-center justify-center text-center border border-blush/40 p-12"
            >
              <div className="h-16 w-16 rounded-full bg-blush flex items-center justify-center text-white mb-6"><Check size={30} /></div>
              <h3 className="font-serif text-4xl text-espresso mb-3">Request received</h3>
              <p className="text-muted-foreground font-light max-w-sm">Thank you! Katie will review your request and any photos, then message you to confirm your clean and arrange the deposit. Check your inbox for a confirmation email.</p>
              <button data-testid="contact-reset" onClick={() => setDone(false)} className="mt-8 text-xs uppercase tracking-[0.22em] text-blush hover:text-espresso transition-colors">
                Send another request
              </button>
            </motion.div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@email.com" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="07000 000000" />
              </div>
              <div>
                <label className={labelCls}>Service</label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="contact-service" className="mt-1 border-0 border-b border-espresso/20 rounded-none bg-transparent px-0 shadow-none focus:ring-0 focus:border-blush text-espresso h-11 font-light">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Preferred Date</label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="contact-date" className={`${inputCls} flex items-center justify-between text-left`}>
                      <span className={date ? "text-espresso" : "text-cocoa/50"}>{date ? format(date, "EEE, d MMM yyyy") : "Pick a day"}</span>
                      <CalendarIcon size={16} className="text-blush" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setDateOpen(false); }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className={labelCls}>Preferred Time</label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="contact-time" className="mt-1 border-0 border-b border-espresso/20 rounded-none bg-transparent px-0 shadow-none focus:ring-0 focus:border-blush text-espresso h-11 font-light">
                    <SelectValue placeholder="Select a time (07:00–20:00)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {BOOKING_TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Property details</label>
                <input data-testid="contact-property" value={form.property_details} onChange={set("property_details")} className={inputCls} placeholder="e.g. 3 bed / 2 bath semi, town" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Anything else?</label>
                <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={2} className={`${inputCls} resize-none`} placeholder="Tell me about the clean you need…" />
              </div>

              {/* File upload */}
              <div className="sm:col-span-2">
                <label className={labelCls}>Photos / walk-through video</label>
                <label data-testid="contact-upload-label" className="mt-2 flex flex-col items-center justify-center gap-2 border border-dashed border-espresso/25 py-8 cursor-pointer hover:border-blush hover:bg-blush-light/40 transition-colors duration-300">
                  <UploadCloud size={26} className="text-blush" />
                  <span className="text-sm text-cocoa font-light">Tap to add photos or a short video (up to 20 files, max 200MB each)</span>
                  <input data-testid="contact-file-input" type="file" accept="image/*,video/*" multiple className="hidden" onChange={addFiles} />
                </label>
                {files.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="contact-file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 bg-card px-4 py-2 text-sm text-espresso">
                        <span className="flex items-center gap-2 truncate">
                          {f.type.startsWith("video") ? <Film size={15} className="text-blush shrink-0" /> : <ImageIcon size={15} className="text-blush shrink-0" />}
                          <span className="truncate font-light">{f.name}</span>
                        </span>
                        <button type="button" onClick={() => removeFile(i)} className="text-cocoa/60 hover:text-espresso transition-colors" aria-label="Remove file"><X size={16} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer select-none">
                <input data-testid="contact-callout" type="checkbox" checked={form.callout} onChange={(e) => setForm((f) => ({ ...f, callout: e.target.checked }))} className="h-4 w-4 accent-blush" />
                <span className="text-sm text-cocoa font-light">I'd prefer Katie to call out to the property for a quote</span>
              </label>

              <div className="sm:col-span-2 pt-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 bg-espresso text-cream px-10 py-5 rounded-full text-xs uppercase tracking-[0.22em] hover:bg-blush transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />}
                  {submitting ? "Sending…" : "Send Booking Request"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
